// Google Maps address picker with autocomplete suggestions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGoogleMaps } from './GoogleMapsConfigProvider';
import { GoogleMapsAddressPickerProps, AddressSuggestion } from './types';
import { Loader2, MapPin, AlertCircle } from 'lucide-react';

export const GoogleMapsAddressPicker: React.FC<GoogleMapsAddressPickerProps> = ({
  value,
  onChange,
  onError,
  placeholder = 'Rechercher une adresse...',
  className = '',
  required = false,
  disabled = false
}) => {
  const { isLoaded, isLoading, error: apiError, service } = useGoogleMaps();
  const [inputValue, setInputValue] = useState(value?.formatted_address || '');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  // Update input value when external value changes
  useEffect(() => {
    if (value?.formatted_address && value.formatted_address !== inputValue) {
      setInputValue(value.formatted_address);
    }
  }, [value?.formatted_address]);

  // Fetch suggestions (debounced) whenever the user types
  useEffect(() => {
    if (!isLoaded || !service || disabled) {
      return;
    }

    const query = inputValue.trim();

    // Nothing to search, or the value already matches a selected address
    if (query.length < 3 || (value && value.formatted_address === inputValue)) {
      setSuggestions([]);
      setIsFetching(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsFetching(true);

    const timer = setTimeout(async () => {
      try {
        const results = await service.autocompleteAddresses(query);
        // Ignore stale responses
        if (requestId !== requestIdRef.current) {
          return;
        }
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (err) {
        if (requestId === requestIdRef.current) {
          setSuggestions([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsFetching(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, isLoaded, service, disabled, value?.formatted_address]);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle manual input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setLocalError(null);

    // If input is cleared, clear the selected address
    if (!newValue.trim()) {
      setSuggestions([]);
      onChange(null);
    }
  };

  // Resolve and apply a suggestion, always writing the exact selected address
  const handleSelectSuggestion = useCallback(
    async (suggestion: AddressSuggestion) => {
      if (!service) {
        return;
      }

      // Show the clicked label immediately, then confirm with structured data
      setInputValue(suggestion.fullText || suggestion.primaryText);
      setSuggestions([]);
      setShowSuggestions(false);
      setLocalError(null);
      setIsResolving(true);

      // Geocoding-fallback suggestions already carry the parsed address.
      if (suggestion.parsedAddress) {
        const displayAddress = suggestion.parsedAddress.formatted_address || suggestion.fullText;
        setInputValue(displayAddress);
        onChange({ ...suggestion.parsedAddress, formatted_address: displayAddress });
        setIsResolving(false);
        return;
      }

      try {
        let parsedAddress = await service.getPlaceAddress(suggestion.placeId);
        if (!parsedAddress) {
          parsedAddress = await service.resolveQuery(suggestion.fullText || suggestion.primaryText);
        }
        if (parsedAddress) {
          const displayAddress = parsedAddress.formatted_address || suggestion.fullText;
          setInputValue(displayAddress);
          onChange({ ...parsedAddress, formatted_address: displayAddress });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Impossible de récupérer les détails de l\'adresse';
        setLocalError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setIsResolving(false);
      }
    },
    [service, onChange, onError]
  );

  // Handle key press (Enter selects the first suggestion when available)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && showSuggestions && suggestions.length > 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[0]);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md bg-gray-50">
          <Loader2 className="w-5 h-5 mr-2 animate-spin text-gray-500" />
          <span className="text-gray-600">Chargement de Google Maps...</span>
        </div>
      </div>
    );
  }

  // Show API error
  if (apiError) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center p-4 border border-red-300 rounded-md bg-red-50">
          <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
          <span className="text-red-600">Google Maps non disponible: {apiError}</span>
        </div>
      </div>
    );
  }

  // Show disabled state
  if (disabled) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={inputValue}
            readOnly
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
            placeholder={placeholder}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => setShowSuggestions(suggestions.length > 0)}
          autoComplete="off"
          className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            localError ? 'border-red-300 bg-red-50' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
          placeholder={placeholder}
          required={required}
          disabled={disabled || !isLoaded}
        />
        {(isFetching || isResolving) && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 animate-spin text-gray-500" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {suggestions.map((suggestion) => (
            <li key={suggestion.placeId}>
              <button
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              >
                <span className="block text-sm font-medium text-gray-800">
                  {suggestion.primaryText}
                </span>
                {suggestion.secondaryText && (
                  <span className="block text-xs text-gray-500">{suggestion.secondaryText}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* No result hint */}
      {showSuggestions && !isFetching && inputValue.trim().length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-4 py-2 text-sm text-gray-500">
          Aucune adresse trouvée
        </div>
      )}

      {/* Error message */}
      {localError && (
        <div className="mt-1 flex items-center text-sm text-red-600">
          <AlertCircle className="w-4 h-4 mr-1" />
          {localError}
        </div>
      )}

      {/* Help text */}
      {!localError && isLoaded && (
        <div className="mt-1 text-xs text-gray-500">
          Commencez à taper une adresse puis sélectionnez une suggestion
        </div>
      )}

      {/* Address preview */}
      {value && !localError && (
        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
          <div className="font-medium text-blue-800">Adresse sélectionnée:</div>
          <div className="text-blue-700">{value.formatted_address}</div>
          {value.latitude && value.longitude && (
            <div className="text-blue-600 text-xs mt-1">
              Coordonnées: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
